import { Card, Input, Select, SelectItem, Textarea } from "@nextui-org/react"
import { useTranslation } from "react-i18next"
import { Control, Controller, UseFieldArrayReturn, useWatch } from "react-hook-form"
import { ScheduleFormData } from "./scheduler-settings"
import { useGetCredentialsListQuery, useGetDifyWorkflowsQuery, useGetFabricPipelinesQuery, useGetLambdaFunctionsQuery, useGetStepFunctionsQuery } from "@circuitry-ai/doc-data"
import { CtyButton, JsonFormatButton, ScheduleConfigurationSection } from "@circuitry-ai/doc-ui"
import { Icon } from "@iconify/react"

type CustomParamMode = 'key_value' | 'object';

export interface CustomParamsFieldProps {
    control: any;
    fields: any[];
    onAppend: () => void;
    onRemove: (index: number) => void;
    getValues: () => any;
    customParamMode: CustomParamMode;
    onCustomParamModeChange: (mode: CustomParamMode) => void;
    customParamJson: string;
    onCustomParamJsonChange: (value: string) => void;
    customParamJsonError: string;
  }

  export interface TargetConfigurationsSectionProps {
    control: Control<ScheduleFormData, any>
    handleSelectChange: any
    customParamsFields: UseFieldArrayReturn<ScheduleFormData, "custom_params", "id">
    getValues: () => any
    customParamMode: CustomParamMode
    onCustomParamModeChange: (mode: CustomParamMode) => void
    customParamJson: string
    onCustomParamJsonChange: (value: string) => void
    customParamJsonError: string
  }

const CustomParamsField: React.FC<CustomParamsFieldProps> = ({
    control, fields, onAppend, onRemove, getValues,
    customParamMode, onCustomParamModeChange, customParamJson, onCustomParamJsonChange, customParamJsonError,
  }) => {
    const { t } = useTranslation();
    const valdateRules=(value:string,index:number,field:string) => {
      const current = getValues();
      const otherValue = current?.custom_params?.[index]?.[field] || '';
      if ((value && !otherValue) || (!value && otherValue)) {
        return t('key and value are required');
      }
      return true;
    }
    return (
      <div className="mt-4 space-y-3">
        <label className="text-sm font-medium text-default-600">{t('Custom Parameters')} <span className="text-default-400 text-xs">({t('optional')})</span></label>
        <Select
          selectedKeys={[customParamMode]}
          onSelectionChange={(keys) => {
            const value = Array.from(keys)[0] as CustomParamMode;
            if (value) onCustomParamModeChange(value);
          }}
          label={t('Parameter Format')}
          size="sm"
        >
          <SelectItem key="key_value">{t('Key Value')}</SelectItem>
          <SelectItem key="object">{t('Object')}</SelectItem>
        </Select>

        {customParamMode === 'key_value' ? (
          <>
            {fields.map((field, index) => (
              <div key={field.id} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <Controller
                  name={`custom_params.${index}.key`}
                  control={control}
                  rules={{
                    validate: (value) => valdateRules(value, index, 'value'),
                  }}
                  render={({ field: inputField, fieldState }) => (
                    <Input
                      {...inputField}
                      label={t('Key')}
                      placeholder={t('e.g., environment')}
                      size="sm"
                      isInvalid={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                    />
                  )}
                />
                <Controller
                  name={`custom_params.${index}.value`}
                  control={control}
                  rules={{
                    validate: (value) => valdateRules(value, index, 'key'),
                  }}
                  render={({ field: inputField, fieldState }) => (
                    <Input
                      {...inputField}
                      label={t('Value')}
                      placeholder={t('e.g., production')}
                      size="sm"
                      isInvalid={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                    />
                  )}
                />
                {fields.length > 1 && (
                  <CtyButton
                    size="sm"
                    variant="light"
                    color="danger"
                    isIconOnly
                    onPress={() => onRemove(index)}
                    aria-label={t('Remove')}
                  >
                    <Icon icon="mdi:delete" />
                  </CtyButton>
                )}
              </div>
            ))}
            <CtyButton
              size="sm"
              variant="bordered"
              onPress={onAppend}
              className="w-full"
            >
              <Icon icon="mdi:plus" className="mr-2" />
              {t('Add Custom Param')}
            </CtyButton>
          </>
        ) : (
          <div>
            <Textarea
              value={customParamJson}
              onChange={(e) => onCustomParamJsonChange(e.target.value)}
              label={
                <div className="flex items-center justify-between w-full gap-4">
                  <span>{t('JSON Object')}</span>
                  <JsonFormatButton
                    value={customParamJson}
                    onChange={onCustomParamJsonChange}
                    disabled={!!customParamJsonError}
                  />
                </div>
              }
              placeholder={t('{"key": "value"}')}
              minRows={4}
              size="sm"
              isInvalid={!!customParamJsonError}
              errorMessage={customParamJsonError}
            />
          </div>
        )}
      </div>
    );
  };

const useScheduleSharedComponents  = () => {
  const { t } = useTranslation();

    const DescriptionField=({control}: any)=><Controller
    name="description"
    control={control}
    render={({ field }) => (
      <Textarea
        {...field}
        label={t('Description')}
        placeholder={t('Optional description for this schedule')}
        minRows={3}
        size="sm"
      />
    )}
  />

 
  const TargetConfigurationsSection=({
    control, handleSelectChange, customParamsFields, getValues,
    customParamMode, onCustomParamModeChange, customParamJson, onCustomParamJsonChange, customParamJsonError,
  }:TargetConfigurationsSectionProps)=>{
    const { data: credentialsList, isLoading: isCredentialsListLoading } = useGetCredentialsListQuery();
    const watchedTargetType = useWatch({ control, name: 'target_type' });
    const { data: workflowData, isLoading: isWorkflowsLoading } = useGetDifyWorkflowsQuery(undefined, { skip: watchedTargetType !== 'agent' });
    const { data: lambdaFunctions, isLoading: isLambdaLoading } = useGetLambdaFunctionsQuery(undefined, { skip: watchedTargetType !== 'lambda' });
    const { data: stepFunctions, isLoading: isStepFunctionsLoading } = useGetStepFunctionsQuery(undefined, { skip: watchedTargetType !== 'step_function' });
    const { data: fabricPipelines, isLoading: isFabricPipelinesLoading } = useGetFabricPipelinesQuery(undefined, { skip: watchedTargetType !== 'fabric' });

    const targetTypeOptions = [
      { label: t('Agent'), value: 'agent' },
      { label: t('Lambda'), value: 'lambda' },
      { label: t('Step Function'), value: 'step_function' },
      { label: t('Fabric'), value: 'fabric' },
    ];

    const getTargetIdOptions = () => {
      if (watchedTargetType === 'agent') {
        return (workflowData || []).map((workflow: any) => ({
          key: workflow.workflow_id,
          label: workflow.workflow_name,
        }));
      }
      if (watchedTargetType === 'lambda') {
        return (lambdaFunctions || []).map((fn: any) => ({
          key: fn.FunctionArn,
          label: fn.FunctionName,
        }));
      }
      if (watchedTargetType === 'step_function') {
        return (stepFunctions || []).map((sm: any) => ({
          key: sm.stateMachineArn,
          label: sm.name,
        }));
      }
      if (watchedTargetType === 'fabric') {
        return (fabricPipelines || []).map((pipeline) => ({
          key: pipeline.id,
          label: pipeline.name,
        }));
      }
      return [];
    };

    const isTargetIdLoading = 
      (watchedTargetType === 'agent' && isWorkflowsLoading) ||
      (watchedTargetType === 'lambda' && isLambdaLoading) ||
      (watchedTargetType === 'step_function' && isStepFunctionsLoading) ||
      (watchedTargetType === 'fabric' && isFabricPipelinesLoading);

    const targetIdOptions = getTargetIdOptions();

    return  <Card className="p-6">
    <h3 className="font-medium text-xl border-b pb-2 mb-4">{t('Target Configuration')}</h3>
    <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-2 items-start">
      <Controller
        name="target_type"
        control={control}
        rules={{ required: t('Target type is required') }}
        render={({ field, fieldState }) => (
          <div className="min-w-0 w-full">
            <Select
              selectedKeys={field.value ? [field.value] : []}
              onSelectionChange={handleSelectChange(field.onChange)}
              label={t('Target Type')}
              placeholder={t('Select target type')}
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              size="sm"
              classNames={{ base: 'w-full' }}
            >
              {targetTypeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </Select>
          </div>
        )}
      />
      <Controller
        name="target_id"
        control={control}
        rules={{ required: t('Target ID is required') }}
        render={({ field, fieldState }) => (
          <div className="min-w-0 w-full">
            <Select
              selectedKeys={field.value ? [field.value] : []}
              onSelectionChange={handleSelectChange(field.onChange)}
              label={t('Target ID')}
              placeholder={watchedTargetType ? t('Select target id') : t('Select target type first')}
              isInvalid={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              size="sm"
              isDisabled={!watchedTargetType}
              isLoading={isTargetIdLoading}
              classNames={{
                base: 'w-full',
                popoverContent: 'overflow-x-auto',
                listbox: 'min-w-max',
              }}
            >
              {targetIdOptions.map((option: { key: string; label: string }) => (
                <SelectItem
                  key={option.key}
                  title={option.label}
                  value={option.key}
                  className="max-w-none"
                >
                  <span className="whitespace-nowrap">{option.label}</span>
                </SelectItem>
              ))}
            </Select>
          </div>
        )}
      />
    </div>
    <div className="mt-4">
      <Controller
        name="credential_id"
        control={control}
        render={({ field }) => (
          <Select
            selectedKeys={field.value ? [field.value] : []}
            onSelectionChange={handleSelectChange(field.onChange)}
            label={t('Credentials')}
            placeholder={t('Select credential (optional)')}
            size="sm"
            isLoading={isCredentialsListLoading}
          >
            {credentialsList?.map((credential) => (
              <SelectItem title={credential.name} key={credential.credential_id} value={credential.credential_id}>{credential.name}</SelectItem>
            )) || []}
          </Select>
        )}
      />
    </div>
    <CustomParamsField
      control={control}
      fields={customParamsFields.fields}
      onAppend={() => customParamsFields.append({ key: '', value: '' })}
      onRemove={(index) => customParamsFields.remove(index)}
      getValues={getValues}
      customParamMode={customParamMode}
      onCustomParamModeChange={onCustomParamModeChange}
      customParamJson={customParamJson}
      onCustomParamJsonChange={onCustomParamJsonChange}
      customParamJsonError={customParamJsonError}
    />
  </Card>
  }

  return {
    DescriptionField,
    TargetConfigurationsSection,
    ScheduleConfigurationSection,
  }
}
export default useScheduleSharedComponents;
